import {
  ChangeEvent,
  ComponentProps,
  HTMLAttributes,
  PropsWithChildren,
} from 'react'
import { TbUpload } from 'react-icons/tb'

import { Popup } from 'components/Popup'
import useAuth from 'contexts/auth'
import { WorkspaceLogo } from 'routes/components/WorkspaceLogo'
import { FileUploaderMaskButton } from 'routes/components/WorkspaceLogoUploader'
import { css, cx } from 'styled-system/css'
import { HStack, Square } from 'styled-system/jsx'
import { Button } from 'ui-kit/button'
import { Field } from 'ui-kit/field'
import { Icon } from 'ui-kit/icon'
import { Text, TextProps } from 'ui-kit/text'

import { LayoutBlackWithLogo } from '../LayoutBlackWithLogo'

export function CreateWorkspacePage({ children }) {
  return (
    <LayoutBlackWithLogo>
      <Popup.Root open>
        <Popup.Window width={'md'} hideCloseButton>
          <Popup.Content
            paddingBlockStart='5'
            paddingBlockEnd='6'
            paddingInline='10'
          >
            {children}
          </Popup.Content>
        </Popup.Window>
      </Popup.Root>
    </LayoutBlackWithLogo>
  )
}

const workspaceFormClassname = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2',
  marginBlockEnd: '1',
})

export function WorkspaceForm({
  children,
  className,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLFormElement>>) {
  return (
    <form className={cx(workspaceFormClassname, className)} {...props}>
      {children}
    </form>
  )
}

export function InputField({
  label,
  value,
  hint,
  onChange,
  ...props
}: {
  label: string
  value: string
  hint?: string
  onChange: (value: string) => void
} & Omit<ComponentProps<typeof Field.Input>, 'onChange'>) {
  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value)
  }
  return (
    <Field.Root gap='0.5'>
      <Field.Label>{label}</Field.Label>
      <Field.Input value={value} onChange={handleChange} {...props} />
      {hint && <Field.HelperText>{hint}</Field.HelperText>}
    </Field.Root>
  )
}

export function TextareaField({
  label,
  value,
  hint,
  onChange,
  ...props
}: {
  label: string
  value: string
  hint?: string
  onChange: (value: string) => void
} & Omit<ComponentProps<typeof Field.Textarea>, 'onChange'>) {
  function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
    onChange(e.target.value)
  }
  return (
    <Field.Root gap='0.5'>
      <Field.Label>{label}</Field.Label>
      <Field.Textarea value={value} onChange={handleChange} {...props} />
      {hint && <Field.HelperText>{hint}</Field.HelperText>}
    </Field.Root>
  )
}

export function LogoField({
  label,
  logoUri,
  onUpload,
}: {
  label: string
  logoUri?: string
  onUpload: (imgBase64: string | undefined) => void
}) {
  return (
    <HStack gap='4' alignItems='start'>
      <FileUploaderMaskButton onUpload={onUpload}>
        {logoUri ? (
          <WorkspaceLogo
            logoUri={logoUri}
            width='16'
            height='16'
            flexShrink='0'
            flexGrow='0'
          />
        ) : (
          <Square
            size='16'
            borderRadius='l3'
            backgroundColor='primary.a9'
          ></Square>
        )}
      </FileUploaderMaskButton>
      <Field.Root gap='1.5'>
        <Field.Label>{label}</Field.Label>
        <HStack gap='4'>
          <FileUploaderMaskButton onUpload={onUpload}>
            <Button
              variant='subtle'
              type='button'
              size='dense'
              pointerEvents='none'
            >
              <Icon>
                <TbUpload />
              </Icon>
              Upload
            </Button>
          </FileUploaderMaskButton>
          <Button
            onClick={() => onUpload(undefined)}
            variant='outline'
            type='button'
            size='dense'
            disabled={!logoUri}
          >
            Remove
          </Button>
        </HStack>
        <Field.HelperText>
          *.png, *.jpeg files up to 10MB at least 400px by 400px
        </Field.HelperText>
      </Field.Root>
    </HStack>
  )
}

export function NotAUserThenLogoutText(props: TextProps) {
  const { self, logout } = useAuth()
  return (
    <Text textAlign='center' {...props}>
      Not {self.user.email}?{' '}
      <Button
        variant='link'
        size='dense'
        type='button'
        textDecoration={{
          base: 'underline',
          _hover: 'none',
        }}
        onClick={logout}
      >
        Logout
      </Button>
    </Text>
  )
}
